import * as React from 'react'
import { useLocation } from 'react-router-dom'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import companies from 'modules/core/store/collections/companies'

import ActivateAddonModal from 'modules/app_store/components/ActivateAddonModal'
import {
  AtsInactiveWarning,
  useAtsInactiveWarning,
} from 'modules/app_store/components/CTAButton/AtsInactiveWarning'
import DependencyInstallModal from 'modules/app_store/components/DependencyInstallModal'
import { UninstallModal } from 'modules/app_store/components/UninstallModal'
import SubscriptionAddonInstaller, {
  type ActivatePayload,
} from 'modules/app_store/lib/SubscriptionAddonInstaller'
import { APPS } from 'modules/billing/lib/types'
import type SubscriptionAddon from 'modules/billing/store/models/SubscriptionAddon'
import RequestUpgradeModal from 'modules/upsell/components/RequestUpgradeModal'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'

interface Props {
  subscriptionAddon: SubscriptionAddon
}

const needsInstallConfiguration = (subscriptionAddon: SubscriptionAddon) => {
  const apps = [
    APPS.ats,
    APPS.expenses,
    APPS.payroll_bundle,
    APPS.competencies_custom_table,
    APPS.complaints,
    APPS.employee_onboarding,
    APPS.desky,
    APPS.linkedin_limited_listings,
    APPS.ats_idealdisc,
  ]

  if (apps.includes(subscriptionAddon.attributes.id)) {
    return true
  }

  if (subscriptionAddon.attributes.id === APPS.ats_careers_page) {
    return !companies.current.attributes.slug
  }

  return false
}

const AddonButton = ({ subscriptionAddon }: Props) => {
  const [upgradePlanModal, setUpgradePlanModal] = React.useState(false)
  const [settingsInstallModal, setSettingsInstallModal] = React.useState(false)
  const [dependencyInstallModal, setDependencyInstallModal] = React.useState(false)
  const [uninstallModal, setUninstallModal] = React.useState(false)
  const { navigation } = useNavigation()
  const location = useLocation()

  const { running, performAction } = useAsyncAction(
    async (payload: ActivatePayload | null | undefined) => {
      const installer = new SubscriptionAddonInstaller(subscriptionAddon, navigation)
      await installer.toggle(payload ?? undefined)
    }
  )

  const inactiveWarning = useAtsInactiveWarning(subscriptionAddon.id)
  if (inactiveWarning) return <AtsInactiveWarning />

  const checkSettingsAndInstall = () => {
    if (needsInstallConfiguration(subscriptionAddon)) {
      return setSettingsInstallModal(true)
    }

    trackAndPerformAction(trackingEvents.appStore.installAnyAppSuccess)
  }

  const checkAndInstall = () => {
    const installer = new SubscriptionAddonInstaller(subscriptionAddon, navigation)

    if (installer.disabledDependee) {
      return setDependencyInstallModal(true)
    }

    checkSettingsAndInstall()
  }

  const onConfirmDependencyInstall = () => {
    setDependencyInstallModal(false)
    checkSettingsAndInstall()
  }

  const showUninstallModal = () => {
    setUninstallModal(true)
  }

  const isInstalled = subscriptionAddon.attributes.enabled

  const trackAndPerformAction = (
    event_name: string,
    payload: ActivatePayload | null | undefined = null
  ) => {
    performAction(payload)
    return track(event_name, {
      app_name: subscriptionAddon?.id,
      app_page_path: location.pathname,
      app_page_url: window.location.href,
    })
  }

  const handleOnClick = () => {
    if (isInstalled) {
      showUninstallModal()
    } else if (!subscriptionAddon.attributes.available_for_current_plan) {
      setUpgradePlanModal(true)
    } else {
      checkAndInstall()
    }
  }

  const ctaButton = () => {
    return isInstalled
      ? i18n.t('app_store_v2.common.cta_button.uninstall')
      : i18n.t('app_store_v2.common.cta_button.install')
  }
  if (hasFeature('dev_packaging_paywalls') && subscriptionAddon.type == 'app') {
    return null
  }

  return (
    <Box flexDirection="row" alignItems="center" gap="s8" paddingY="s16">
      <Button
        type={isInstalled ? 'secondary' : 'primary'}
        disabled={running}
        onClick={handleOnClick}
      >
        {ctaButton()}
      </Button>
      {!isInstalled && subscriptionAddon.attributes.available_for_current_plan && (
        <Text size="100" weight="medium">
          {i18n.t('app_store_v2.common.cta_info.install')}
        </Text>
      )}
      {upgradePlanModal && (
        <RequestUpgradeModal
          requestReason={ValidReasons.UpgradeFromLegacy}
          requestSource="marketplace-legacy-apps"
          onClose={() => setUpgradePlanModal(false)}
        />
      )}
      {dependencyInstallModal && (
        <DependencyInstallModal
          subscriptionAddon={subscriptionAddon}
          onSubmit={onConfirmDependencyInstall}
          onClose={() => setDependencyInstallModal(false)}
        />
      )}
      {settingsInstallModal && (
        <ActivateAddonModal
          subscriptionAddon={subscriptionAddon}
          onSuccess={(payload: ActivatePayload | null | undefined = null) =>
            trackAndPerformAction(trackingEvents.appStore.installAnyAppSuccess, payload)
          }
          onClose={() => setSettingsInstallModal(false)}
        />
      )}
      {uninstallModal && (
        <UninstallModal
          onClose={() => setUninstallModal(false)}
          onSubmit={() => trackAndPerformAction(trackingEvents.appStore.uninstallAnyAppSuccess)}
          subscriptionAddon={subscriptionAddon}
        />
      )}
    </Box>
  )
}

export default AddonButton
